import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { buildTooltip } from "./HeatmapTooltip";
import { DARK_THEME } from "./HeatmapTheme";
import { StockEntry } from "./Heatmap";

const TOOLTIP_OFFSET = 14;

export interface TooltipState {
  stock: StockEntry;
  x: number;
  y: number;
}

const FloatingTooltip: React.FC<{
  tooltip: TooltipState | null;
  theme: typeof DARK_THEME;
  isDark: boolean;
}> = ({ tooltip, theme, isDark }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: 0, top: 0 });

  useEffect(() => {
    if (!tooltip || !ref.current) return;
    const { width: tw, height: th } = ref.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = tooltip.x + TOOLTIP_OFFSET;
    let top = tooltip.y + TOOLTIP_OFFSET;
    if (left + tw > vw - 8) left = tooltip.x - tw - TOOLTIP_OFFSET;
    if (top + th > vh - 8) top = tooltip.y - th - TOOLTIP_OFFSET;

    setPos({ left, top });
  }, [tooltip]);

  if (!tooltip) return null;

  // Portaled to <body> rather than rendered in place: the heat-tile
  // panel (Heatmap.tsx) carries its own `backdrop-filter` for the glass
  // look, and a `backdrop-filter`/`filter` on an ancestor makes THAT
  // element the containing block for any `position: fixed` descendant
  // instead of the viewport (same CSS rule DynamicGraphs' stock-search
  // popup already had to portal around — see stockSelectOptions.tsx's
  // own comment on this). Without the portal, `left`/`top` here (
  // computed from clientX/clientY, i.e. viewport-relative) were being
  // applied relative to the panel's own box instead, offsetting the
  // tooltip by however far the panel itself sits from the viewport's
  // top-left corner — the "shows far from the mouse" bug.
  return createPortal(
    <div
      ref={ref}
      style={{
        position: "fixed",
        left: pos.left,
        top: pos.top,
        zIndex: 9999,
        pointerEvents: "none",
      }}
      dangerouslySetInnerHTML={{
        __html: buildTooltip(tooltip.stock, theme, isDark),
      }}
    />,
    document.body,
  );
};

export default FloatingTooltip;
